import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Calendar, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

const getMetricInputFields = (metricKey) => {
  const fields = {
    mrr: [{key: 'monthly_revenue', label: 'Monthly Revenue ($)', type: 'number'}],
    cac: [
      {key: 'marketing_spend', label: 'Marketing Spend ($)', type: 'number'},
      {key: 'customers_acquired', label: 'Customers Acquired', type: 'number'}
    ],
    churn: [
      {key: 'churned_customers', label: 'Churned Customers', type: 'number'},
      {key: 'total_customers', label: 'Total Customers', type: 'number'}
    ],
    ltv: [
      {key: 'average_revenue_per_user', label: 'Average Revenue per User ($)', type: 'number'},
      {key: 'average_customer_lifespan', label: 'Average Customer Lifespan (months)', type: 'number'}
    ],
    burn_rate: [{key: 'monthly_expenses', label: 'Monthly Expenses ($)', type: 'number'}],
    runway: [
      {key: 'current_cash', label: 'Current Cash ($)', type: 'number'},
      {key: 'monthly_burn', label: 'Monthly Burn ($)', type: 'number'}
    ],
    growth: [
      {key: 'new_customers', label: 'Current Customers', type: 'number'},
      {key: 'previous_customers', label: 'Previous Customers', type: 'number'}
    ],
    pmf: [{key: 'very_disappointed_percentage', label: 'Very Disappointed % (Sean Ellis)', type: 'number'}]
  };
  return fields[metricKey] || [];
};

const WeeklyDataEntry = ({ startupId, onEntrySubmitted }) => {
  const [selectedMetric, setSelectedMetric] = useState('');
  const [inputs, setInputs] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const metrics = [
    { key: 'mrr', name: 'Monthly Recurring Revenue' },
    { key: 'cac', name: 'Customer Acquisition Cost' },
    { key: 'churn', name: 'Churn Rate' },
    { key: 'ltv', name: 'Lifetime Value' },
    { key: 'burn_rate', name: 'Burn Rate' },
    { key: 'runway', name: 'Cash Runway' },
    { key: 'growth', name: 'Customer Growth Rate' },
    { key: 'pmf', name: 'Product-Market Fit Score' }
  ];

  const getCurrentWeek = () => {
    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);
    const weekNumber = Math.ceil((((now - yearStart) / 86400000) + yearStart.getDay() + 1) / 7);
    return { week: weekNumber, year: now.getFullYear() };
  };

  const currentWeek = getCurrentWeek();
  const inputFields = selectedMetric ? getMetricInputFields(selectedMetric) : [];

  const handleInputChange = (key, value) => {
    setInputs({ ...inputs, [key]: parseFloat(value) || 0 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedMetric) {
      toast.error('Please select a metric');
      return;
    }

    const requiredFields = getMetricInputFields(selectedMetric).map(f => f.key);
    const missingFields = requiredFields.filter(field => !inputs[field]);
    
    if (missingFields.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/startups/${startupId}/weekly-entry`,
        {
          metric_key: selectedMetric,
          inputs: inputs
        },
        { withCredentials: true }
      );
      
      toast.success(`Week ${currentWeek.week} data submitted successfully!`);
      setInputs({});
      setSelectedMetric('');
      if (onEntrySubmitted) onEntrySubmitted();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to submit weekly data';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="bg-slate-800/40 border-slate-700 p-6" data-testid="weekly-data-entry">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
          <Calendar className="w-5 h-5 text-cyan-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-100">Submit Weekly Data</h3>
          <p className="text-sm text-slate-400">Week {currentWeek.week}, {currentWeek.year}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Metric Selection */}
        <div>
          <Label className="text-slate-300">Select Metric *</Label>
          <select
            value={selectedMetric}
            onChange={(e) => {
              setSelectedMetric(e.target.value);
              setInputs({});
            }}
            data-testid="metric-select"
            className="w-full mt-1 bg-slate-800 border border-slate-700 text-slate-100 rounded-md px-3 py-2"
          >
            <option value="">-- Choose Metric --</option>
            {metrics.map(m => (
              <option key={m.key} value={m.key}>{m.name}</option>
            ))}
          </select>
        </div>

        {/* Input Fields */}
        {inputFields.length > 0 && (
          <div className="space-y-3">
            {inputFields.map(field => (
              <div key={field.key}>
                <Label htmlFor={field.key} className="text-slate-300 text-sm">{field.label}</Label>
                <Input
                  id={field.key}
                  data-testid={`weekly-input-${field.key}`}
                  type={field.type}
                  value={inputs[field.key] || ''}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  className="bg-slate-800 border-slate-700 text-slate-100 mt-1"
                  required
                />
              </div>
            ))}
          </div>
        )}

        {/* Submit Button */}
        {selectedMetric && (
          <Button
            type="submit"
            disabled={submitting}
            data-testid="submit-weekly-data"
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {submitting ? 'Submitting...' : `Submit Week ${currentWeek.week} Data`}
          </Button>
        )}
      </form>
    </Card>
  );
};

export default WeeklyDataEntry;
